import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_BASE || '').replace(/\/api$/, '') || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

export default function WhatsAppCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', templateId: '', audienceId: '', scheduleNow: true });
  const [detail, setDetail] = useState(null);
  const [progress, setProgress] = useState(null);

  const token = localStorage.getItem('dentalToken');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([
        axios.get(`${API}/api/saas/wa-campaigns`, { headers }),
        axios.get(`${API}/api/saas/wa-templates?status=approved`, { headers })
      ]);
      setCampaigns(c.data.campaigns || []);
      setTemplates(t.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openWizard = async () => {
    try {
      const { data } = await axios.get(`${API}/api/saas/wa-templates`, { headers });
      setTemplates(data || []);
    } catch {}
    setStep(1);
    setForm({ name: '', templateId: '', audienceId: '', scheduleNow: true });
    setShowWizard(true);
  };

  const createAndLaunch = async () => {
    if (!form.name || !form.templateId || !form.audienceId) return alert('All fields required');
    try {
      const { data: campaign } = await axios.post(`${API}/api/saas/wa-campaigns`, {
        name: form.name,
        templateId: form.templateId,
        audienceId: form.audienceId,
        scheduling: { sendAt: form.scheduleNow ? new Date().toISOString() : null }
      }, { headers });

      if (form.scheduleNow) {
        await axios.post(`${API}/api/saas/wa-campaigns/${campaign._id}/launch`, {}, { headers });
        alert(`Campaign launched!`);
      } else {
        alert(`Campaign saved as draft.`);
      }
      setShowWizard(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to launch');
    }
  };

  const viewDetail = async (campaign) => {
    setDetail(campaign);
    try {
      const { data } = await axios.get(`${API}/api/saas/wa-campaigns/${campaign._id}/progress`, { headers });
      setProgress(data);
    } catch {}
  };

  const createRetry = async (id) => {
    if (!window.confirm('Create retry campaign with failed contacts?')) return;
    try {
      const { data } = await axios.post(`${API}/api/saas/wa-campaigns/${id}/create-retry`, {}, { headers });
      alert(`Retry campaign created with ${data.failedCount} contacts`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  const statusColor = (s) => ({ draft: '#f59e0b', running: '#3b82f6', completed: '#10b981', failed: '#ef4444' })[s] || '#888';

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📢 WhatsApp Campaigns</h2>
        <button onClick={openWizard} style={btnPrimary}>+ New Campaign</button>
      </div>

      {loading ? <div>Loading...</div> : (
        <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={th}>Name</th>
                <th style={th}>Status</th>
                <th style={th}>Total</th>
                <th style={th}>Sent</th>
                <th style={th}>Failed</th>
                <th style={th}>Created</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c._id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={td}>{c.name}</td>
                  <td style={td}>
                    <span style={{ background: statusColor(c.status), color: 'white', padding: '2px 8px', borderRadius: 12, fontSize: 11 }}>{c.status}</span>
                  </td>
                  <td style={td}>{c.stats?.totalContacts || 0}</td>
                  <td style={td}>{c.stats?.sent || 0}</td>
                  <td style={{ ...td, color: c.stats?.failed ? '#ef4444' : '#666' }}>{c.stats?.failed || 0}</td>
                  <td style={td}>{new Date(c.createdAt).toLocaleDateString()}</td>
                  <td style={td}>
                    <button onClick={() => viewDetail(c)} style={miniBtn}>View</button>
                    {c.stats?.failed > 0 && (
                      <button onClick={() => createRetry(c._id)} style={{ ...miniBtn, background: '#f59e0b', color: 'white' }}>↻ Retry Failed</button>
                    )}
                  </td>
                </tr>
              ))}
              {campaigns.length === 0 && (
                <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#888', padding: 40 }}>
                  No campaigns yet. Create your first broadcast.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showWizard && (
        <Modal onClose={() => setShowWizard(false)} title={`New Campaign — Step ${step}/3`}>
          {step === 1 && (
            <>
              <label style={lbl}>Campaign Name</label>
              <input placeholder="e.g. Diwali Offer 2026" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={input} />
              <label style={lbl}>Template</label>
              <select value={form.templateId} onChange={e => setForm({ ...form, templateId: e.target.value })} style={input}>
                <option value="">-- Select Template --</option>
                {templates.map(t => <option key={t._id} value={t._id}>{t.name} ({t.status})</option>)}
              </select>
              <button onClick={() => form.name && form.templateId ? setStep(2) : alert('Fill name + template')} style={btnPrimary}>Next</button>
            </>
          )}
          {step === 2 && (
            <>
              <label style={lbl}>Audience ID (from Contacts page)</label>
              <input placeholder="Paste audience _id" value={form.audienceId} onChange={e => setForm({ ...form, audienceId: e.target.value })} style={input} />
              <p style={{ fontSize: 12, color: '#666' }}>Create audiences in Contacts page first.</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setStep(1)} style={btnSecondary}>Back</button>
                <button onClick={() => form.audienceId ? setStep(3) : alert('Audience required')} style={btnPrimary}>Next</button>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <label style={lbl}>Schedule</label>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <input type="radio" checked={form.scheduleNow} onChange={() => setForm({ ...form, scheduleNow: true })} /> Launch now
              </label>
              <label style={{ display: 'block', marginBottom: 16 }}>
                <input type="radio" checked={!form.scheduleNow} onChange={() => setForm({ ...form, scheduleNow: false })} /> Save as draft
              </label>
              <div style={{ background: '#f9fafb', padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
                <div><strong>Name:</strong> {form.name}</div>
                <div><strong>Template:</strong> {templates.find(t => t._id === form.templateId)?.name}</div>
                <div><strong>Audience:</strong> {form.audienceId}</div>
                <div><strong>DND respected:</strong> 9pm–9am IST auto-queued</div>
                <div><strong>Rate limit:</strong> 80 msgs/sec</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setStep(2)} style={btnSecondary}>Back</button>
                <button onClick={createAndLaunch} style={btnPrimary}>{form.scheduleNow ? '🚀 Launch' : 'Save Draft'}</button>
              </div>
            </>
          )}
        </Modal>
      )}

      {detail && (
        <Modal onClose={() => { setDetail(null); setProgress(null); }} title={`📊 ${detail.name}`}>
          {progress ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              <Stat label="Total" value={progress.stats.total} color="#374151" />
              <Stat label="Sent" value={progress.stats.sent} color="#3b82f6" />
              <Stat label="Delivered" value={progress.stats.delivered} color="#10b981" />
              <Stat label="Read" value={progress.stats.read} color="#8b5cf6" />
              <Stat label="Replied" value={progress.stats.replied} color="#06b6d4" />
              <Stat label="Failed" value={progress.stats.failed} color="#ef4444" />
            </div>
          ) : <div>Loading stats...</div>}
        </Modal>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#f9fafb', padding: 16, borderRadius: 6, textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

const input = { padding: 10, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, marginBottom: 12, width: '100%', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#374151' };
const btnPrimary = { padding: '8px 16px', background: '#25D366', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const btnSecondary = { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' };
const miniBtn = { padding: '4px 10px', fontSize: 12, marginRight: 6, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' };
const th = { padding: 12, textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#374151' };
const td = { padding: 12, fontSize: 14 };
